import { Vector2, randomShuffle } from '../../../odie';
import { ColorId } from '../../defs/block';
import { setBlockEntityViewLayer } from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { IEffect } from './IEffect';
import { RadialAttackEffect } from './RadialAttackEffect';
import StateObserver, { user } from 'src/StateObserver';
import { lollipopLimit } from 'src/replicant/match2/races/races.ruleset';
import { getCubeTargets, getCubeTargetGroups } from '../../util/matchTools';
import { SuperCubeSingleAnimation } from '../../animations/SuperCubeSingleAnimation';
import { SuperCubeDoubleAnimation } from '../../animations/SuperCubeDoubleAnimation';
import { waitFor } from '../../../lib/util/waitFor';
import { app } from '../../app';

export type SuperCubeEffectOptions = {
  position: Vector2;
  color: ColorId;
};

export class SuperCubeEffect implements IEffect {
  private readonly scene: GameScene;
  private options: SuperCubeEffectOptions;

  constructor(scene: GameScene, options: SuperCubeEffectOptions) {
    this.scene = scene;
    this.options = options;
  }

  public static assets(options: Partial<SuperCubeEffectOptions>): string[] {
    return [];
  }

  public async execute() {
    if (this.options.color === 'all') {
      await this.executeDoubleCubeEffect();
    } else {
      await this.executeSingleCubeEffect();
    }
  }

  private async executeSingleCubeEffect() {
    const { color, position } = this.options;
    const targets = getCubeTargets(this.scene, color);
    const targetsGroups = getCubeTargetGroups(targets);
    const collisionTracker = getCollisionTracker();

    const animation = new SuperCubeSingleAnimation({
      color,
      position,
      targets,
    });

    const entity = spawnOverlayEntity(
      this.scene,
      position,
      { width: 1, height: 1 },
      () => animation,
    );

    setBlockEntityViewLayer(this.scene, entity, 'foreground');

    animation.start();

    for (const targetGroup of targetsGroups) {
      // Shuffle the targets order to make the attack more visually appealing
      const shuffledfTargets = randomShuffle(targetGroup.slice());
      await animation.fireBeamsAtTargets(shuffledfTargets);
      for (const target of shuffledfTargets) {
        collisionTracker.collide(target.c.block, 'attack', { type: 'cube' });
      }
      animation.clearBeams();
    }

    animation.explode().then(() => {
      animation.stop();
      despawnOverlayEntity(this.scene, entity);
    });

    await StateObserver.invoke.addMatch2RaceScore({
      goal: 'lollipop',
      score: targets.length,
    });

    this.scene.events.publish({
      id: 'group',
      cause: 'cube',
      count: targets.length,
    });
  }

  private async executeDoubleCubeEffect() {
    // Create the radial sweep attack effect
    const grid = this.scene.mapDef?.grid;
    const dist = grid ? Math.max(grid.columns, grid.rows) : 9;
    const radius = Math.ceil(Math.sqrt(dist * dist + dist * dist));
    const effect = new RadialAttackEffect(this.scene, {
      position: this.options.position,
      radius,
      duration: 0.6,
      collider: { type: 'cubeAll' },
    });

    // Set up the animation
    const animation = new SuperCubeDoubleAnimation();
    const entity = spawnOverlayEntity(
      this.scene,
      this.options.position,
      { width: 1, height: 1 },
      () => animation,
    );
    // This overlay should go on top of the other foreground overlays
    entity.c.view2d.view.zIndex = 1;
    setBlockEntityViewLayer(this.scene, entity, 'foreground');

    // Run the first explosition: animate then execute the attack effect
    await animation.start();
    await waitFor(1000);
    animation.pulse();
    await effect.execute();

    // Trigger a "fall" phase to make blocks fall before the second explosion
    this.scene.phaseSystem.phase = 'fall';
    await waitFor(1000);

    // Run the second explosition and finish the animation
    animation.pulse();
    effect.execute();
    await waitFor(500);

    animation.explode().then(() => {
      animation.stop();
      despawnOverlayEntity(this.scene, entity);
    });

    await StateObserver.invoke.addMatch2RaceScore({
      goal: 'lollipop',
      score: Math.min(effect.targetCount, lollipopLimit),
    });
  }
}
